import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getFeaturedArticles, api, getCurrentSponsor, getHomepageSettings } from '../utils/api';
import { isNewsletterSubscribed, markNewsletterSubscribed } from '../utils/newsletterPrefs';
import { useAuth } from '../context/AuthContext';
import EventCard from '../components/events/EventCard';
import HeroEventWall from '../components/events/HeroEventWall';
import ArticleCard from '../components/magazine/ArticleCard';
import { useTheme } from '../context/ThemeContext';
import useWindowWidth from '../hooks/useWindowWidth';
import { LocationIcon } from '../components/common/InlineIcons';
import { SPACING, BORDER_RADIUS, SHADOWS, TRANSITIONS, TYPOGRAPHY, RESPONSIVE_PADDING } from '../theme/designTokens';
import '../components/common/CardHoverEffect.css';
import SponsoredBlock from '../components/SponsoredBlock';

const CITY_LIST = ['Houston', 'Atlanta', 'New York', 'Toronto', 'Washington DC', 'Miami', 'Dallas', 'Chicago', 'Los Angeles', 'Montreal', 'Philadelphia'];

// Helper for consistent responsive padding
const responsivePadding = (isMobile, isTablet) =>
  isMobile ? `${SPACING.xxxl}px 16px` : isTablet ? `${SPACING.lg}px 24px` : `${SPACING.xxxl}px 40px`;

function isTonight(dateStr) {
  const today = new Date();
  const d = new Date(dateStr);
  return d.getFullYear() === today.getFullYear() &&
         d.getMonth() === today.getMonth() &&
         d.getDate() === today.getDate();
}

function EventCardSkeleton() {
  return (
    <div style={{
      flexShrink: 0, width: 240, borderRadius: BORDER_RADIUS.standard,
      overflow: 'hidden', background: '#151210',
    }}>
      <div style={{
        width: '100%', aspectRatio: '1 / 1',
        background: '#241F18',
        animation: 'skeleton-pulse 1.5s ease-in-out infinite',
      }} />
      <div style={{ padding: '12px 14px' }}>
        <div style={{ height: 14, width: '80%', borderRadius: 6, background: '#241F18', marginBottom: 8, animation: 'skeleton-pulse 1.5s ease-in-out infinite' }} />
        <div style={{ height: 11, width: '55%', borderRadius: 6, background: '#2C261D', animation: 'skeleton-pulse 1.5s ease-in-out infinite' }} />
      </div>
      <style>{`
        @keyframes skeleton-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}

function EventRail({ events, loading, tonightIds }) {
  const railStyle = { display: 'flex', gap: SPACING.md, overflowX: 'auto', paddingBottom: 8, scrollSnapType: 'x mandatory', scrollBehavior: 'smooth', scrollbarWidth: 'none' };
  const inner = loading
    ? [...Array(4)].map((_, i) => <EventCardSkeleton key={i} />)
    : events.map(ev => (
        <div key={ev._id} className="vb-event-card" style={{ flexShrink: 0, width: 'clamp(240px,72vw,260px)', position: 'relative', scrollSnapAlign: 'start' }}>
          {tonightIds && tonightIds.includes(ev._id?.toString()) && (
            <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 10, background: '#E63946', color: '#fff', fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 4, letterSpacing: 0.5, boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}>
              TONIGHT
            </div>
          )}
          <EventCard event={ev} />
        </div>
      ));
  return (
    <>
      <style>{`
        .vb-snap-rail::-webkit-scrollbar{display:none}
        .vb-event-card { transition: transform ${TRANSITIONS.fast}; }
      `}</style>
      <div className="rail vb-snap-rail" style={railStyle}>{inner}</div>
    </>
  );
}

// Consistent SectionHead — eyebrow + headline + "View all →"
// Only HAPPENING NOW gets a pulsing red dot.
function SectionHead({ eyebrow, title, sub, to, viewAllLabel = 'View all', showLiveDot = false }) {
  const { VB } = useTheme();
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: SPACING.lg, flexWrap: 'wrap', gap: SPACING.sm }}>
      <div>
        {eyebrow && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
            {showLiveDot && (
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: VB.red, display: 'inline-block', animation: 'pulse 1.5s infinite' }} />
            )}
            <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: VB.gold, fontFamily: 'var(--font-body)', margin: 0 }}>{eyebrow}</p>
          </div>
        )}
        <h2 style={{ ...TYPOGRAPHY.heading2, fontSize: 34, color: VB.snowPrimary, fontFamily: 'var(--font-display)', fontWeight: 600, margin: 0, letterSpacing: '-0.025em', lineHeight: 1.08 }}>{title}</h2>
        {sub && <p style={{ fontSize: 13, color: VB.snowMuted, marginTop: 4, margin: 0 }}>{sub}</p>}
      </div>
      {to && (
        <Link to={to} style={{ fontSize: 13, fontWeight: 600, color: VB.snowMuted, textDecoration: 'underline', textDecorationColor: 'rgba(255,255,255,0.25)', textUnderlineOffset: 4, flexShrink: 0, letterSpacing: 0.2, transition: `color ${TRANSITIONS.fast}` }}
          onMouseEnter={e => { e.currentTarget.style.color = VB.snowPrimary; }}
          onMouseLeave={e => { e.currentTarget.style.color = VB.snowMuted; }}>
          {viewAllLabel}
        </Link>
      )}
    </div>
  );
}

// Compact article row for the above-fold "Trending Articles" column.
function TrendingArticleRow({ article, last }) {
  const { VB } = useTheme();
  const author = article.authorName
    || (typeof article.author === 'object' ? (article.author?.name || article.author?.displayName) : article.author)
    || 'Vibana Editorial';
  const readTime = article.readTime
    ? (/read/i.test(String(article.readTime)) ? article.readTime : /\bmin\b/i.test(String(article.readTime)) ? `${article.readTime} read` : `${article.readTime} min read`)
    : '';
  return (
    <Link
      to={`/magazine/${article.slug || article._id}`}
      style={{
        textDecoration: 'none',
        display: 'flex',
        gap: SPACING.sm,
        padding: `${SPACING.sm}px ${SPACING.md}px`,
        borderBottom: !last ? `0.5px solid ${VB.cardBorderSubtle}` : 'none',
        transition: `background ${TRANSITIONS.fast}`,
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(251,244,230,0.05)'; e.currentTarget.style.boxShadow = 'inset 3px 0 0 var(--gold)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      {article.coverImage ? (
        <img
          src={article.coverImage}
          alt={article.title}
          style={{ width: 74, height: 74, borderRadius: 12, objectFit: 'cover', flexShrink: 0, border: `1px solid ${VB.borderDark}` }}
          onError={e => { e.target.src = 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=200'; }}
        />
      ) : (
        <div style={{ width: 72, height: 72, borderRadius: BORDER_RADIUS.compact, background: VB.darkCardAlt, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: VB.lightMuted }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        {article.category && (
          <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: VB.gold, marginBottom: 5, display: 'block' }}>
            {String(article.category).replace(/-/g, ' ')}
          </span>
        )}
        <p style={{ fontSize: 16, fontWeight: 500, fontFamily: 'var(--font-display)', fontOpticalSizing: 'auto', letterSpacing: '-0.015em', color: VB.lightPrimary, margin: '0 0 5px', lineHeight: 1.2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {article.title}
        </p>
        <div style={{ fontSize: 11, color: VB.lightMuted, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>By {author}</span>
          {readTime && <><span>{'·'}</span><span>{readTime}</span></>}
        </div>
      </div>
    </Link>
  );
}

// Above-fold sponsor banner (right column) — surfaces an active sponsor.
function SponsorColumn({ banner }) {
  /*
   * The homepage slot, now the same component as every other placement.
   *
   * It used to be its own markup — a gold-tinted box with a logo, a name and
   * an underlined "Learn more about our partners". Perfectly fine on its own
   * and the problem was that it was on its own: three sponsor surfaces across
   * the site, three different looks, so sponsorship read as three products
   * rather than one.
   *
   * variant="banner", not a column — I had this wrong. This slot is not a
   * grid cell beside anything: it is a full-width band in its own section
   * that ALTERNATES with <NewsletterColumn strip />, which is itself a
   * horizontal row. A 260px-tall column here was both the wrong shape for
   * the slot and much bigger than the thing it replaces.
   */
  return (
    <SponsoredBlock
      sponsorId={banner.id || banner._id}
      name={banner.sponsorName}
      logo={banner.sponsorLogo}
      image={banner.image}
      headline={banner.sponsorTagline || banner.headline}
      buttonText={banner.buttonText || 'Learn more'}
      goPath={banner.goPath}
      website={banner.sponsorWebsite || banner.linkUrl}
      label="Presented by"
      variant="banner"
    />
  );
}

export function SubscribedAppTile({ variant = 'column' }) {
  const { VB } = useTheme();
  const compact = variant === 'compact';
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: compact ? 8 : SPACING.sm,
      padding: compact ? 16 : SPACING.lg,
      background: 'linear-gradient(180deg, rgba(230,57,70,0.08) 0%, rgba(0,0,0,0.0) 100%), ' + VB.darkCard,
      border: `1px solid ${VB.borderDark}`,
      borderRadius: BORDER_RADIUS.large,
      height: compact ? 'auto' : '100%',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          width: 22, height: 22, borderRadius: 6,
          background: 'rgba(16,185,129,0.18)', color: '#10B981',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, fontWeight: 800,
        }}>✓</span>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: '#10B981', textTransform: 'uppercase' }}>
          You're in
        </span>
      </div>
      <h3 style={{ ...TYPOGRAPHY.heading3, color: VB.snowPrimary, margin: 0, fontSize: compact ? 16 : undefined, fontFamily: 'var(--font-display)' }}>
        Get live drops on the app.
      </h3>
      <p style={{ fontSize: 13, color: VB.snowMuted, margin: 0, lineHeight: 1.5 }}>
        Last-minute events, push alerts, and your saved list — straight to your pocket.
      </p>
      <div style={{ display: 'flex', gap: 8, marginTop: compact ? 4 : 'auto', flexWrap: 'wrap' }}>
        <a
          href="https://apps.apple.com/app/vibana"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            flex: '1 1 120px',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            gap: 6, padding: '10px 12px',
            background: '#000', color: '#fff', textDecoration: 'none',
            borderRadius: BORDER_RADIUS.compact, fontSize: 12, fontWeight: 700,
            border: `1px solid ${VB.borderDark}`,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
          App Store
        </a>
        <a
          href="https://play.google.com/store/apps/details?id=app.vibana"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            flex: '1 1 120px',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            gap: 6, padding: '10px 12px',
            background: '#000', color: '#fff', textDecoration: 'none',
            borderRadius: BORDER_RADIUS.compact, fontSize: 12, fontWeight: 700,
            border: `1px solid ${VB.borderDark}`,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M3 20.5V3.5c0-.49.27-.91.69-1.13l13.62 9.62L3.69 21.63c-.42-.22-.69-.64-.69-1.13M19.5 12l-3.05-1.76-3.5 3.06 3.5 3.06L19.5 14.5c.83-.46.83-1.54 0-2M14.74 10.97L4.93 4.16 14.74 11l3.5-3.06-3.5-3.06"/></svg>
          Google Play
        </a>
      </div>
    </div>
  );
}

// Above-fold newsletter signup column (default when no sponsor).
function NewsletterColumn({ strip = false }) {
  const { VB } = useTheme();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  // Remember subscribers across visits so we stop showing a form they
  // already filled in.
  useEffect(() => { setSubscribed(isNewsletterSubscribed()); }, []);

  if (subscribed) return <SubscribedAppTile />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/.+@.+\..+/.test(email)) { setStatus('error'); return; }
    setStatus('loading');
    try {
      await api.post('/newsletter/subscribe', { email });
      markNewsletterSubscribed();
      setSubscribed(true);
      setEmail('');
    } catch {
      // Silent: keep optimistic UX even if endpoint is missing
      markNewsletterSubscribed();
      setSubscribed(true);
      setEmail('');
    }
  };

  if (strip) {
    return (
      <form onSubmit={submit} style={{
        display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap',
        padding: `${SPACING.lg}px ${SPACING.xl}px`,
        background: 'linear-gradient(100deg, rgba(233,162,59,0.10), rgba(230,57,70,0.05))',
        border: `1px solid ${VB.goldBorder}`, borderRadius: BORDER_RADIUS.large,
      }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: VB.gold, textTransform: 'uppercase' }}>The Drop · Newsletter</span>
          <h3 style={{ ...TYPOGRAPHY.heading3, color: VB.snowPrimary, margin: '3px 0 0', fontFamily: 'var(--font-display)' }}>Get the week ahead.</h3>
        </div>
        <div style={{ display: 'flex', gap: 8, flex: 1, minWidth: 260, alignItems: 'center' }}>
          <input
            type="email" placeholder="you@email.com" value={email}
            onChange={e => { setEmail(e.target.value); setStatus(''); }}
            aria-label="Email address"
            style={{ flex: 1, height: 44, padding: `0 ${SPACING.md}px`, borderRadius: BORDER_RADIUS.compact, border: `1px solid ${status === 'error' ? VB.red : VB.borderDark}`, background: VB.darkCard, color: VB.snowPrimary, fontSize: 14, outline: 'none', fontFamily: 'var(--font-body)' }}
          />
          <button type="submit" disabled={status === 'loading'}
            style={{ height: 44, padding: '0 22px', background: VB.red, color: '#FFFFFF', border: 'none', borderRadius: BORDER_RADIUS.compact, fontWeight: 700, fontSize: 14, cursor: status === 'loading' ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
            {status === 'success' ? 'Subscribed' : status === 'loading' ? 'Subscribing…' : 'Subscribe'}
          </button>
        </div>
        {status === 'error' && <span style={{ fontSize: 11, color: VB.red, flexBasis: '100%' }}>Please enter a valid email.</span>}
      </form>
    );
  }

  return (
    <form
      onSubmit={submit}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: SPACING.sm,
        padding: SPACING.lg,
        background: VB.glassBg,
        border: `1px solid ${VB.glassBorder || VB.borderDark}`,
        borderRadius: BORDER_RADIUS.large,
        flex: 1,
      }}
    >
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: VB.gold, textTransform: 'uppercase' }}>The Drop</span>
      <h3 style={{ ...TYPOGRAPHY.heading3, color: VB.snowPrimary, margin: 0, fontFamily: 'var(--font-display)' }}>
        Get the week ahead.
      </h3>
      <p style={{ fontSize: 13, color: VB.snowMuted, margin: 0, lineHeight: 1.5 }}>
        Hand-picked African events &amp; stories — straight to your inbox every Thursday.
      </p>
      <input
        type="email"
        placeholder="you@email.com"
        value={email}
        onChange={e => { setEmail(e.target.value); setStatus(''); }}
        aria-label="Email address"
        style={{
          height: 42,
          padding: `0 ${SPACING.sm}px`,
          borderRadius: BORDER_RADIUS.compact,
          border: `1px solid ${status === 'error' ? VB.red : VB.borderDark}`,
          background: VB.darkCard,
          color: VB.snowPrimary,
          fontSize: 14,
          outline: 'none',
          fontFamily: 'var(--font-body)',
        }}
      />
      <button
        type="submit"
        disabled={status === 'loading'}
        style={{
          height: 42,
          background: VB.red,
          color: '#FFFFFF',
          border: 'none',
          borderRadius: BORDER_RADIUS.compact,
          fontWeight: 700,
          fontSize: 14,
          cursor: status === 'loading' ? 'wait' : 'pointer',
          transition: `opacity ${TRANSITIONS.fast}`,
        }}
        onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; }}
        onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
      >
        {status === 'success' ? 'Subscribed' : status === 'loading' ? 'Subscribing…' : 'Subscribe'}
      </button>
      {status === 'error' && <span style={{ fontSize: 11, color: VB.red }}>Please enter a valid email.</span>}
    </form>
  );
}

// City typeahead — text input with dropdown matches.
function CityTypeahead({ value, onChange, isMobile }) {
  const { VB } = useTheme();
  const [draft, setDraft] = useState(value || '');
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => { setDraft(value || ''); }, [value]);

  useEffect(() => {
    const handler = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const matches = useMemo(() => {
    const q = draft.trim().toLowerCase();
    if (!q) return CITY_LIST.slice(0, 6);
    return CITY_LIST.filter(c => c.toLowerCase().includes(q)).slice(0, 6);
  }, [draft]);

  const choose = (c) => {
    setDraft(c);
    onChange(c);
    setOpen(false);
  };

  return (
    <div ref={boxRef} style={{ position: 'relative', width: '100%', maxWidth: isMobile ? '100%' : 460 }}>
      <div style={{ position: 'relative' }}>
        <span style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: VB.red, display: 'flex', alignItems: 'center', pointerEvents: 'none' }}>
          <LocationIcon size={18} strokeWidth={2.2} />
        </span>
        <input
          value={draft}
          onChange={e => { setDraft(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={e => {
            if (e.key === 'Enter') { onChange(draft.trim()); setOpen(false); }
            if (e.key === 'Escape') { setOpen(false); }
          }}
          placeholder="Pick a city — Houston, Atlanta, Toronto…"
          aria-label="City"
          style={{
            width: '100%',
            height: 56,
            paddingLeft: 48,
            paddingRight: draft ? 96 : 16,
            borderRadius: BORDER_RADIUS.full,
            border: `1px solid ${VB.borderDark}`,
            background: VB.darkCard,
            color: VB.snowPrimary,
            fontSize: 15,
            outline: 'none',
            fontFamily: 'var(--font-body)',
            boxShadow: SHADOWS.subtle,
            transition: `border-color ${TRANSITIONS.fast}`,
          }}
          onFocusCapture={e => { e.target.style.borderColor = VB.red; }}
          onBlurCapture={e => { e.target.style.borderColor = VB.borderDark; }}
        />
        {draft && (
          <button
            type="button"
            onClick={() => { setDraft(''); onChange(''); setOpen(false); }}
            style={{ position: 'absolute', right: 60, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: VB.snowMuted, cursor: 'pointer', padding: 8, display: 'flex', alignItems: 'center' }}
            aria-label="Clear city"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        )}
        <button
          type="button"
          onClick={() => { onChange(draft.trim()); setOpen(false); }}
          style={{
            position: 'absolute', right: 6, top: 6, bottom: 6,
            background: VB.red, color: '#FFFFFF',
            border: 'none', borderRadius: BORDER_RADIUS.full,
            padding: '0 18px',
            fontWeight: 700, fontSize: 13,
            cursor: 'pointer',
            transition: `opacity ${TRANSITIONS.fast}`,
          }}
          onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
        >
          Go
        </button>
      </div>
      {open && matches.length > 0 && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', left: 0, right: 0,
          background: VB.darkCard, borderRadius: BORDER_RADIUS.standard,
          border: `1px solid ${VB.borderDark}`, boxShadow: SHADOWS.elevated,
          zIndex: 100, overflow: 'hidden',
        }}>
          {matches.map((c, i) => (
            <button
              key={c}
              type="button"
              onMouseDown={() => choose(c)}
              style={{
                display: 'flex', alignItems: 'center', gap: SPACING.sm,
                width: '100%',
                padding: `${SPACING.sm}px ${SPACING.md}px`,
                background: 'transparent',
                border: 'none',
                borderBottom: i < matches.length - 1 ? `0.5px solid ${VB.borderDark}` : 'none',
                color: VB.snowPrimary,
                fontSize: 14,
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'var(--font-body)',
                transition: `background ${TRANSITIONS.fast}`,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(230,57,70,0.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{ color: VB.red, display: 'flex' }}>
                <LocationIcon size={14} strokeWidth={2.2} />
              </span>
              {c}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function HomePage() {
  const { VB } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;
  // Above-fold 3-column collapses to stack on mobile <900px
  const isStacked = windowWidth < 900;

  const [tonightEvents, setTonightEvents]   = useState([]);
  const [trendingEvents, setTrendingEvents] = useState([]);
  const [followFeedEvents, setFollowFeedEvents] = useState([]);
  const [articles, setArticles]             = useState([]);
  const [activeCity, setActiveCity]         = useState('');
  const [sponsorSlot, setSponsorSlot]       = useState(null);
  const [homepageSettings, setHomepageSettings] = useState(null);
  // Null when healthy, otherwise the name of the rail that failed. It used to
  // be a boolean with no setter back to false anywhere in this file, so a
  // single failed request latched the banner on for the whole session and the
  // only way out was a full page reload.
  const [loadError, setLoadError]           = useState(null);
  const [reloadKey, setReloadKey]           = useState(0);
  const [railsLoading, setRailsLoading]     = useState(true);

  // Fetch tonight events immediately with timezone awareness
  useEffect(() => {
    // /events/featured used to be fetched here and stashed in a featuredEvent
    // state. That state was removed when the admin-picked hero moved into
    // /admin/homepage-settings, but the fetch and its setter call were left
    // behind — so setFeaturedEvent was an undeclared identifier, and every
    // response carrying at least one featured event threw a ReferenceError
    // inside .then(). The .catch() swallowed it and reported a network
    // failure, which is why the banner accused a healthy endpoint.

    const tzOffset = -(new Date().getTimezoneOffset() / 60);
    api.get('/events/happening-now', { params: { type: 'tonight', tz: tzOffset } }).then(r => {
      const evs = r.data?.data || r.data || [];
      setTonightEvents(evs);
    }).catch(e => console.warn("[silent catch]", e?.message || e));
  }, [reloadKey]);

  // Fetch static data once
  useEffect(() => {
    let cancelled = false;

    Promise.allSettled([
      api.get('/events/trending'),
      getFeaturedArticles(),
      getCurrentSponsor(),
      getHomepageSettings(),
    ]).then(([trending, arts, sponsor, settings]) => {
      if (cancelled) return;
      if (trending.status === 'fulfilled') {
        const evs = Array.isArray(trending.value.data) ? trending.value.data : [];
        setTrendingEvents(evs);
      }
      if (arts.status === 'fulfilled') setArticles(arts.value.data || []);
      if (sponsor.status === 'fulfilled') setSponsorSlot(sponsor.value.data);
      if (settings.status === 'fulfilled') {
        const s = settings.value.data;
        setHomepageSettings(s);
      }
      if (trending.status === 'rejected') {
        console.error('[home] /events/trending failed:', trending.reason?.response?.status, trending.reason?.message);
        setLoadError('trending events');
      }
      setRailsLoading(false);
    });

    return () => { cancelled = true; };
  }, [reloadKey]);

  // Personalized data for logged-in users
  useEffect(() => {
    if (!user) { setFollowFeedEvents([]); return; }
    api.get('/feed/following')
      .then(r => setFollowFeedEvents(Array.isArray(r.data?.events) ? r.data.events : []))
      .catch(e => console.warn("[silent catch]", e?.message || e));
  }, [user?._id]);

  // City switch — fetch tonight + upcoming in chosen city
  const handleCitySwitch = (c) => {
    // The hero city picker is a shortcut into discovery: pick a city and go
    // straight to the Events page filtered to it (rather than re-filtering home).
    if (c) { navigate('/events?city=' + encodeURIComponent(c)); return; }
    // Clearing the picker just resets the homepage's default (no-city) view.
    setActiveCity('');
  };

  // Active sponsor banner — admin override else SponsorSlot
  const activeSponsorBanner = homepageSettings?.sponsorBanner?.enabled
    ? homepageSettings.sponsorBanner
    : sponsorSlot;

  const hasActiveSponsor = !!(activeSponsorBanner && (activeSponsorBanner.enabled !== false) && (activeSponsorBanner.sponsorName || activeSponsorBanner.sponsorLogo));

  // Admin-curated homepage rail (configured in admin → Homepage → Article Rail).
  const articleRail = homepageSettings?.articleRail;
  const railItems = articleRail?.enabled
    ? [
        ...(articleRail.articles || []).map(a => ({ kind: 'article', data: a })),
        ...(articleRail.customItems || []).map(c => ({ kind: c.type || 'custom', data: c })),
      ].slice(0, articleRail.maxItems || 6)
    : [];
  const railCardWidth = Math.max(180, Math.min(600, articleRail?.cardWidth ?? 280));
  const railCardGap = Math.max(0, Math.min(48, articleRail?.cardGap ?? 16));

  return (
    <div style={{ background: 'transparent', overflowX: 'hidden' }}>
      <Helmet>
        <title>Vibana — African Events Near You</title>
        <meta name="description" content="Discover African cultural events, music, food, art and stories from the diaspora — near you. Browse events in Houston, Atlanta, New York, Toronto, and more." />
        <link rel="canonical" href="https://vibana.app/" />
        <meta property="og:title" content="Vibana — African Events Near You" />
        <meta property="og:description" content="Discover African cultural events, music, food, art and stories from the diaspora — near you." />
        <meta property="og:url" content="https://vibana.app/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://vibana.app/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://vibana.app/og-image.jpg" />
      </Helmet>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.55; transform: scale(1); }
        }
      `}</style>

      {/* Load error banner */}
      {loadError && (
        <div style={{ background: 'rgba(230,57,70,0.14)', borderBottom: '1px solid rgba(230,57,70,0.28)', padding: '10px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ color: '#EF5A5C', fontSize: 14 }}>Could not load {loadError}.</span>
          <button
            onClick={() => { setLoadError(null); setReloadKey(k => k + 1); }}
            style={{ background: 'none', border: '1px solid #EF4444', borderRadius: BORDER_RADIUS.compact, padding: '4px 12px', color: '#EF4444', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Refresh
          </button>
        </div>
      )}

      {/* HERO — Headline + city typeahead + trust bar */}
      <section style={{
        background: 'linear-gradient(180deg, #0D0B09 0%, #0A0908 100%)',
        padding: isMobile ? '40px 16px 30px' : isTablet ? '56px 24px 40px' : '72px 40px 52px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Warm ambient horizon glow + faint film grain for immersive depth */}
        <div aria-hidden="true" style={{ position: 'absolute', top: '-30%', right: '-10%', width: '62%', height: '155%', background: 'radial-gradient(closest-side, rgba(217,168,59,0.30), rgba(230,57,70,0.12) 50%, transparent 74%)', pointerEvents: 'none', zIndex: 0 }} />
        <div aria-hidden="true" style={{ position: 'absolute', bottom: '-40%', left: '-12%', width: '48%', height: '140%', background: 'radial-gradient(closest-side, rgba(217,168,59,0.14), transparent 70%)', pointerEvents: 'none', zIndex: 0 }} />
        <div className="vb-grain" aria-hidden="true" style={{ opacity: 0.4 }} />
        {(() => {
          // The wall mirrors the landing page hero. It only earns its space on
          // wide layouts, and only once there are enough real events to fill two
          // drifting columns — otherwise the hero falls back to a single column.
          const wallEvents = isStacked
            ? []
            : [...(trendingEvents || []), ...(tonightEvents || [])]
                .filter((ev, i, all) => ev && all.findIndex(o => o._id === ev._id) === i);
          const showWall = wallEvents.length >= 4;
          return (
            <div style={{
              maxWidth: 1220, margin: '0 auto', position: 'relative', zIndex: 1,
              display: showWall ? 'grid' : 'block',
              gridTemplateColumns: showWall ? '1.02fr 1fr' : undefined,
              gap: 44, alignItems: 'center',
            }}>
              <div>
                <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.24em', textTransform: 'uppercase', color: VB.gold, margin: `0 0 ${SPACING.lg}px`, fontFamily: 'var(--font-body)' }}>
                  The home of African diaspora events
                </p>
                <h1 style={{
                  fontSize: showWall ? 'clamp(42px, 5.2vw, 68px)' : 'clamp(42px, 7.6vw, 84px)',
                  fontWeight: 500,
                  color: VB.lightPrimary,
                  lineHeight: 1.0,
                  letterSpacing: '-0.035em',
                  margin: `0 0 ${SPACING.lg}px`,
                  fontFamily: 'var(--font-display)',
                  fontOpticalSizing: 'auto',
                  maxWidth: 960,
                }}>
                  Discover African<br /><em style={{ fontStyle: 'italic', fontWeight: 500 }}>Culture</em> Events
                </h1>
                <p style={{ fontSize: 17.5, lineHeight: 1.6, color: 'rgba(251,244,230,0.85)', margin: `0 0 ${SPACING.xl}px`, maxWidth: 620 }}>
                  Concerts, food festivals, art shows, and cultural experiences from African and diaspora communities — near you.
                </p>

                {/* City typeahead */}
                <CityTypeahead value={activeCity} onChange={handleCitySwitch} isMobile={isMobile} />
              </div>

              {showWall && <HeroEventWall events={wallEvents} />}
            </div>
          );
        })()}
      </section>

      {/* SECTION RHYTHM — Trending Events rail */}
      {trendingEvents.length > 0 && (
        <section style={{ padding: isMobile ? `${SPACING.xxxl}px 16px` : isTablet ? `${SPACING.xxxl}px 24px` : `48px 40px`, background: 'transparent' }}>
          <div style={{ maxWidth: 1220, margin: '0 auto' }}>
            <SectionHead
              eyebrow="TRENDING NOW"
              title="Popular Events"
              sub="What's trending this week."
              to="/events"
            />
            <EventRail events={trendingEvents.slice(0, 8)} loading={railsLoading} />
          </div>
        </section>
      )}

      {user && followFeedEvents.length > 0 && (
        <section style={{ background: 'transparent', padding: isMobile ? `${SPACING.xxxl}px 16px` : isTablet ? `${SPACING.xxxl}px 24px` : `48px 40px` }}>
          <div style={{ maxWidth: 1220, margin: '0 auto' }}>
            <SectionHead eyebrow="FOLLOWING" title="From Organizers You Follow" to="/following" />
            <EventRail events={followFeedEvents.slice(0, 8)} loading={false} />
          </div>
        </section>
      )}

      {/* STORIES & IDEAS — trending magazine reads, relocated to its own full-width band */}
      {articles.length > 0 && (
        <section style={{ padding: isMobile ? `${SPACING.xxxl}px 16px` : isTablet ? `${SPACING.xxxl}px 24px` : `48px 40px`, background: 'transparent', borderTop: `1px solid ${VB.borderDark}` }}>
          <div style={{ maxWidth: 1220, margin: '0 auto' }}>
            <SectionHead
              eyebrow="TRENDING"
              title="Stories &amp; Ideas"
              sub="What people are reading."
              to="/magazine"
            />
            <div style={{ background: VB.darkCard, borderRadius: 18, border: `1px solid ${VB.borderDark}`, boxShadow: VB.cardShadow, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: isStacked ? '1fr' : '1fr 1fr', columnGap: SPACING.lg }}>
                {articles.slice(0, 4).map((art, idx, arr) => {
                  // On the 2-col grid, the bottom row shouldn't draw a divider under it.
                  const perRow = isStacked ? 1 : 2;
                  const isLastRow = idx >= arr.length - (arr.length % perRow || perRow);
                  return <TrendingArticleRow key={art._id} article={art} last={isLastRow} />;
                })}
              </div>
              <Link to="/magazine" style={{ padding: '14px 16px', borderTop: `1px solid ${VB.borderDark}`, color: VB.gold, fontSize: 12, fontWeight: 700, letterSpacing: '0.03em', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>Read all stories →</Link>
            </div>
          </div>
        </section>
      )}

      {/* SECTION RHYTHM — Admin-curated Article Rail (Homepage → Article Rail) */}
      {railItems.length > 0 && (
        <section style={{ padding: isMobile ? `${SPACING.xxxl}px 16px` : isTablet ? `${SPACING.xxxl}px 24px` : `56px 40px`, background: 'transparent', borderTop: `1px solid ${VB.borderDark}` }}>
          <div style={{ maxWidth: 1220, margin: '0 auto' }}>
            <SectionHead
              eyebrow="FEATURED"
              title={articleRail?.title || 'Featured'}
              to="/magazine"
              viewAllLabel="See more"
            />
            <div className="vb-snap-rail" style={{ display: 'flex', gap: railCardGap, overflowX: 'auto', paddingBottom: 8, scrollSnapType: 'x mandatory', scrollBehavior: 'smooth', scrollbarWidth: 'none' }}>
              <style>{`.vb-snap-rail::-webkit-scrollbar{display:none}`}</style>
              {railItems.map((item, idx) => {
                const cardBase = { flexShrink: 0, width: railCardWidth, scrollSnapAlign: 'start', borderRadius: 14, overflow: 'hidden', background: VB.darkCard, border: `1px solid ${VB.borderDark}`, textDecoration: 'none', display: 'block' };
                if (item.kind === 'article') {
                  const a = item.data;
                  return (
                    <Link key={a._id || idx} to={`/magazine/${a.slug || a._id}`} style={cardBase}>
                      {a.coverImage
                        ? <img src={a.coverImage} alt={a.title} style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block' }} />
                        : <div style={{ width: '100%', aspectRatio: '16/9', background: 'rgba(230,57,70,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#E63946', fontWeight: 900, fontSize: 28 }}>V</div>}
                      <div style={{ padding: 16 }}>
                        {a.category && <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: VB.gold, marginBottom: 8 }}>{a.category}</div>}
                        <div style={{ fontSize: 18, fontWeight: 500, fontFamily: 'var(--font-display)', fontOpticalSizing: 'auto', letterSpacing: '-0.02em', color: VB.snowPrimary, lineHeight: 1.18, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{a.title}</div>
                        {a.excerpt && <div style={{ fontSize: 13, color: VB.snowMuted, marginTop: 7, lineHeight: 1.55, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{a.excerpt}</div>}
                      </div>
                    </Link>
                  );
                }
                // custom rail item: video | logo | custom
                const c = item.data;
                const href = c.videoUrl || c.linkUrl || '#';
                const img = c.imageUrl || c.logoUrl;
                const heading = c.title || c.brandName || '';
                const isVideo = item.kind === 'video';
                return (
                  <a key={idx} href={href} target="_blank" rel="noopener noreferrer" style={cardBase}>
                    <div style={{ position: 'relative' }}>
                      {img
                        ? <img src={img} alt={heading} style={{ width: '100%', aspectRatio: '16/9', objectFit: item.kind === 'logo' ? 'contain' : 'cover', background: item.kind === 'logo' ? '#fff' : 'transparent', display: 'block' }} />
                        : <div style={{ width: '100%', aspectRatio: '16/9', background: 'rgba(255,255,255,0.04)' }} />}
                      {isVideo && (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <div style={{ width: 46, height: 46, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 18 }}>▶</div>
                        </div>
                      )}
                    </div>
                    <div style={{ padding: 14 }}>
                      {isVideo && <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: VB.gold, marginBottom: 6 }}>Video</div>}
                      {heading && <div style={{ fontSize: 15, fontWeight: 800, color: VB.snowPrimary, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{heading}</div>}
                      {c.description && <div style={{ fontSize: 13, color: VB.snowMuted, marginTop: 6, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{c.description}</div>}
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* SECTION RHYTHM — Sponsors strip */}
      {hasActiveSponsor && (
        <section style={{ background: VB.goldTintDk, padding: isMobile ? '20px 16px' : '24px 40px', borderTop: `1px solid ${VB.goldBorder}`, borderBottom: `1px solid ${VB.goldBorder}` }}>
          <div style={{ maxWidth: 1220, margin: '0 auto', display: 'flex', alignItems: 'center', gap: SPACING.md, flexWrap: 'wrap' }}>
            <span style={{ color: VB.gold, fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', fontFamily: 'var(--font-display)' }}>Brought to you by</span>
            {activeSponsorBanner.sponsorLogo && (
              <img src={activeSponsorBanner.sponsorLogo} alt={activeSponsorBanner.sponsorName || ''} style={{ height: 30, objectFit: 'contain', borderRadius: 4 }} />
            )}
            {activeSponsorBanner.sponsorName && (
              <span style={{ color: VB.snowPrimary, fontSize: 15, fontWeight: 700 }}>{activeSponsorBanner.sponsorName}</span>
            )}
            {activeSponsorBanner.sponsorWebsite && (
              <a href={activeSponsorBanner.sponsorWebsite} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 'auto', color: VB.snowMuted, fontSize: 13, fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 4, textDecorationColor: 'rgba(255,255,255,0.25)' }}>
                Visit site
              </a>
            )}
          </div>
        </section>
      )}

      {/* SECTION RHYTHM — App download CTA (cream contrast section) */}
      <section style={{
        background: '#F5EFE3',
        padding: isMobile ? '52px 16px' : isTablet ? '68px 24px' : `${SPACING.xxxl * 2.75}px 40px`,
      }}>
        <div style={{ maxWidth: 1220, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.1fr 1fr', gap: isMobile ? SPACING.xl : 48, alignItems: 'center' }}>
          <div>
            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#C2673F', margin: `0 0 ${SPACING.md}px`, fontFamily: 'var(--font-body)' }}>
              Get the app
            </p>
            <h2 style={{
              fontSize: 'clamp(34px, 5vw, 54px)',
              fontWeight: 600,
              color: '#17110D',
              margin: `0 0 ${SPACING.md}px`,
              fontFamily: 'var(--font-display)',
              fontOpticalSizing: 'auto',
              lineHeight: 1.04,
              letterSpacing: '-0.03em',
            }}>
              The diaspora in your pocket.
            </h2>
            <p style={{ fontSize: 16.5, color: 'rgba(26,22,19,0.64)', margin: `0 0 ${SPACING.xl}px`, lineHeight: 1.6, maxWidth: 520 }}>
              RSVP, save events, and never miss a show. Get Vibana free for iOS and Android.
            </p>
            <div style={{ display: 'flex', gap: SPACING.sm, flexWrap: 'wrap' }}>
              <Link
                to="/download"
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  padding: '14px 24px',
                  borderRadius: 8,
                  background: '#0D0D0D', color: '#FFFFFF',
                  textDecoration: 'none', fontWeight: 700, fontSize: 15,
                  transition: `opacity ${TRANSITIONS.fast}`,
                  ...(isMobile ? { width: '100%' } : {}),
                }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
              >
                Download for iOS
              </Link>
              <Link
                to="/download"
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  padding: '14px 24px',
                  borderRadius: 8,
                  background: 'transparent', color: '#0D0D0D',
                  border: '1.5px solid #0D0D0D',
                  textDecoration: 'none', fontWeight: 700, fontSize: 15,
                  transition: `background ${TRANSITIONS.fast}`,
                  ...(isMobile ? { width: '100%' } : {}),
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.06)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                Download for Android
              </Link>
            </div>
          </div>
          {!isMobile && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{
                width: '100%',
                maxWidth: 320,
                aspectRatio: '9 / 16',
                borderRadius: 32,
                background: 'linear-gradient(135deg, #E63946 0%, #1a0a0b 100%)',
                boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                border: '8px solid #0D0D0D',
                position: 'relative',
                overflow: 'hidden',
              }}>
                <div style={{
                  position: 'absolute',
                  inset: 16,
                  borderRadius: 24,
                  background: 'linear-gradient(180deg, rgba(13,13,13,0.1) 0%, rgba(13,13,13,0.6) 100%)',
                  display: 'flex',
                  alignItems: 'flex-end',
                  padding: 24,
                }}>
                  <div>
                    <p style={{ fontSize: 11, color: VB.gold, margin: 0, fontWeight: 700, letterSpacing: '0.22em' }}>VIBANA</p>
                    <p style={{ fontSize: 26, color: '#FFFFFF', margin: '8px 0 0', fontWeight: 500, fontFamily: 'var(--font-display)', fontOpticalSizing: 'auto', lineHeight: 1.08, letterSpacing: '-0.02em' }}>Your culture.<br /><em style={{ fontStyle: 'italic', fontWeight: 500 }}>Live.</em></p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* THE DROP — newsletter, moved out of the fold to the bottom so it no longer clutters the hero */}
      <section style={{ padding: responsivePadding(isMobile, isTablet), borderTop: `1px solid ${VB.borderDark}` }}>
        <div style={{ maxWidth: 1220, margin: '0 auto' }}>
          {hasActiveSponsor ? <SponsorColumn banner={activeSponsorBanner} /> : <NewsletterColumn strip />}
        </div>
      </section>

    </div>
  );
}
